import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Clock, ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { useCountUp, useTimeGreeting } from '../hooks/useAnimations';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FAMILY_IMAGE = "https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/a2rrfpek_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2001_57_56%20PM.png";

const departments = [
  { en: 'Obstetrics & Gynaecology', ta: 'மகப்பேறு & மகளிர் நலம்' },
  { en: 'Urology', ta: 'சிறுநீரகவியல்' },
  { en: 'Cardiology', ta: 'இதயவியல்' },
  { en: 'Orthopaedics', ta: 'எலும்பியல்' },
  { en: 'Gastroenterology', ta: 'இரைப்பை குடலியல்' },
  { en: 'ENT', ta: 'காது மூக்கு தொண்டை' },
  { en: 'Paediatrics', ta: 'குழந்தை நலம்' },
  { en: 'Spine & Neurology', ta: 'முதுகெலும்பு & நரம்பியல்' },
  { en: 'Diabetology', ta: 'நீரிழிவு' },
  { en: 'General Check-up', ta: 'பொது பரிசோதனை' },
];

const HeroSection = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', department: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.department) {
      toast.error(language === 'ta' ? 'அனைத்து புலங்களையும் நிரப்பவும்' : 'Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        name: formData.name,
        phone: formData.phone,
        email: `${formData.phone}@callback.sakthi`,
        preferred_date: new Date().toISOString().split('T')[0],
        preferred_time: 'Callback Requested',
        department: formData.department,
        message: 'Quick Appointment - Hero Banner',
        language,
      });
      // GTM & Google Ads conversion tracking
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'form_submission',
          form_name: 'hero_quick_appointment',
          form_department: formData.department,
          form_source: 'homepage_hero',
        });
      }
      if (window.gtag) {
        window.gtag('event', 'conversion', { send_to: 'AW-17542370694/hero_appointment' });
      }
      setSuccess(true);
      toast.success(language === 'ta' ? 'நன்றி! விரைவில் தொடர்பு கொள்வோம்.' : 'Thank you! We will call you shortly.');
      setFormData({ name: '', phone: '', department: '' });
      setTimeout(() => setSuccess(false), 5000);
    } catch {
      toast.error(language === 'ta' ? 'பிழை. மீண்டும் முயற்சிக்கவும்.' : 'Error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const greeting = useTimeGreeting(language);

  const features = [
    { value: '25+', label: language === 'ta' ? 'சிறப்புகள்' : 'Specialities', sub: language === 'ta' ? 'ஒரே கூரையின் கீழ்' : 'Under One Roof' },
    { value: '24/7', label: language === 'ta' ? 'அவசர சிகிச்சை' : 'Emergency', sub: language === 'ta' ? '& ICU பராமரிப்பு' : '& ICU Care' },
    { value: '2L+', label: language === 'ta' ? 'குடும்பங்கள்' : 'Happy Families', sub: language === 'ta' ? 'நம்பிக்கை' : 'Trust Us' },
    { value: 'NABH', label: language === 'ta' ? 'அங்கீகாரம்' : 'Accredited', sub: language === 'ta' ? 'தரமான பராமரிப்பு' : 'Quality Care' },
  ];

  return (
    <section id="home" className="relative bg-white overflow-hidden" data-testid="hero-section">
      <div className="relative">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-5 items-stretch min-h-[540px] lg:min-h-[580px]">
            {/* Left Content - 3 cols */}
            <div className="lg:col-span-3 flex flex-col justify-center px-6 sm:px-10 lg:px-14 py-10 lg:py-14 bg-gradient-to-br from-[#f8fafc] via-white to-[#eef4fb] animate-gradient relative z-10">
              <div className="mb-1">
                <span className="text-sm font-medium text-[#0066b3]">{greeting}</span>
              </div>
              <div className="mb-4">
                <h1 className="text-3xl sm:text-4xl lg:text-[2.8rem] xl:text-5xl font-extrabold leading-[1.15] tracking-tight" data-testid="hero-main-tagline">
                  <span className="text-[#1a1a1a] block">
                    {language === 'ta' ? 'சென்னையின் சிறந்த மருத்துவமனை' : 'Best Hospital in Chennai'}
                  </span>
                  <span className="text-[#c41e3a] block">
                    {language === 'ta' ? 'திருவல்லிக்கேணி & ராயப்பேட்டை' : 'Triplicane & Royapettah'}
                  </span>
                </h1>
                <p className="text-lg sm:text-xl text-[#4a5568] italic mt-3 font-medium">
                  {language === 'ta' ? 'குடும்பம் போல பராமரிப்பு. நம்பிக்கையுடன் குணப்படுத்துதல்.' : 'Caring like Family. Healing with Trust.'}
                </p>
                <div className="w-16 h-1 bg-[#d4a24c] mt-3 rounded-full"></div>
              </div>

              <p className="text-base text-[#4a5568] leading-relaxed max-w-md mb-6">
                {language === 'ta'
                  ? 'NABH அங்கீகாரம் பெற்ற பல்துறை சிறப்பு மருத்துவமனை. 25+ ஆண்டுகளாக 2,00,000+ குடும்பங்கள் நம்புகின்றன. திருவல்லிக்கேணி, ராயப்பேட்டை, மயிலாப்பூர், சேப்பாக் & மண்டவெலியில்.'
                  : 'NABH Accredited multi-specialty hospital trusted by 2,00,000+ families for 25+ years. Serving Triplicane, Royapettah, Mylapore, Chepauk & Mandaveli with world-class healthcare.'}
              </p>

              {/* Feature stats row */}
              <div className="grid grid-cols-4 gap-2">
                {features.map((f) => (
                  <div key={f.value} className="text-center p-2.5 bg-white rounded-xl border border-gray-100 shadow-sm" data-testid={`hero-feature-${f.value}`}>
                    <div className="text-lg font-extrabold text-[#c41e3a]">{f.value}</div>
                    <div className="text-[11px] font-semibold text-[#1a2b3c] leading-tight">{f.label}</div>
                    <div className="text-[9px] text-[#64748b]">{f.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Form over image - 2 cols */}
            <div className="lg:col-span-2 relative">
              {/* Background image */}
              <div className="absolute inset-0 hidden lg:block">
                <img src={FAMILY_IMAGE} alt="Family at Sakthi Hospital" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/70 via-[#0a1628]/50 to-[#0a1628]/80"></div>
              </div>

              {/* Form */}
              <div className="relative z-10 flex items-center justify-center h-full p-6 lg:p-8">
                <div className="w-full max-w-sm bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 border border-white/50" data-testid="hero-appointment-form">
                  {success ? (
                    <div className="text-center py-6">
                      <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <CheckCircle size={28} className="text-emerald-600" />
                      </div>
                      <h3 className="text-lg font-bold text-[#0f172a] mb-1">
                        {language === 'ta' ? 'நன்றி!' : 'Thank You!'}
                      </h3>
                      <p className="text-sm text-[#64748b]">
                        {language === 'ta' ? 'எங்கள் குழு விரைவில் தொடர்பு கொள்ளும்' : 'Our team will call you shortly'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="text-center mb-5">
                        <h3 className="text-lg font-bold text-[#0f172a]">
                          {language === 'ta' ? 'விரைவு சந்திப்பு' : 'Quick Appointment'}
                        </h3>
                        <p className="text-xs text-[#64748b] mt-0.5">
                          {language === 'ta' ? 'நாங்கள் விரைவில் அழைப்போம்' : "We'll call you back shortly"}
                        </p>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-3">
                        <input type="hidden" id="zc_gad" name="zc_gad" value="" />
                        <Input
                          type="text"
                          placeholder={language === 'ta' ? 'உங்கள் பெயர்' : 'Your Name'}
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="h-12 px-4 border-gray-200 rounded-lg text-sm bg-white focus:border-[#0066b3] focus:ring-1 focus:ring-[#0066b3]"
                          required
                          data-testid="hero-form-name"
                        />
                        <Input
                          type="tel"
                          placeholder={language === 'ta' ? 'தொலைபேசி எண்' : 'Phone Number'}
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          className="h-12 px-4 border-gray-200 rounded-lg text-sm bg-white focus:border-[#0066b3] focus:ring-1 focus:ring-[#0066b3]"
                          required
                          data-testid="hero-form-phone"
                        />
                        <Select value={formData.department} onValueChange={(v) => setFormData({...formData, department: v})}>
                          <SelectTrigger className="h-12 px-4 border-gray-200 rounded-lg text-sm bg-white focus:border-[#0066b3] focus:ring-1 focus:ring-[#0066b3]" data-testid="hero-form-department">
                            <SelectValue placeholder={language === 'ta' ? 'துறையை தேர்ந்தெடுக்கவும்' : 'Select Department'} />
                          </SelectTrigger>
                          <SelectContent>
                            {departments.map((d) => (
                              <SelectItem key={d.en} value={d.en}>{language === 'ta' ? d.ta : d.en}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Button
                          type="submit"
                          disabled={loading}
                          className="w-full h-12 bg-[#c41e3a] hover:bg-[#a0182f] text-white text-sm font-semibold rounded-lg shadow-md shadow-[#c41e3a]/20 btn-ripple"
                          data-testid="hero-form-submit"
                        >
                          {loading ? (
                            <><Loader2 size={16} className="animate-spin mr-2" /> {language === 'ta' ? 'சமர்ப்பிக்கிறது...' : 'Booking...'}</>
                          ) : (
                            <>{language === 'ta' ? 'இப்போது முன்பதிவு செய்யுங்கள்' : 'Book Now'} <ArrowRight size={16} className="ml-2" /></>
                          )}
                        </Button>
                      </form>

                      <div className="flex items-center justify-center gap-4 mt-4">
                        <span className="flex items-center gap-1 text-[11px] text-[#5a6b7c]">
                          <CheckCircle size={12} className="text-emerald-500" />
                          {language === 'ta' ? 'இலவச ஆலோசனை' : 'Free Consultation'}
                        </span>
                        <span className="flex items-center gap-1 text-[11px] text-[#5a6b7c]">
                          <CheckCircle size={12} className="text-emerald-500" />
                          {language === 'ta' ? 'விரைவான பதில்' : 'Quick Response'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Mobile: show form without image bg */}
              <div className="lg:hidden bg-[#f0f4f8] p-6">
                {/* Already shown above in the relative container */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Strip */}
      <div className="bg-gradient-to-r from-[#c41e3a] to-[#dc2626] text-white py-2.5 relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm font-medium">
            <a href="tel:+914428442626" onClick={() => { if(window.dataLayer) window.dataLayer.push({event: 'click_to_call', call_source: 'hero_emergency_strip', call_number: '+914428442626'}); }} className="flex items-center gap-2 hover:underline">
              <Phone size={14} className="animate-pulse" />
              <span>Emergency: +91 44 2844 2626</span>
            </a>
            <div className="hidden md:flex items-center gap-2">
              <Clock size={14} />
              <span>24/7 Emergency & ICU Services</span>
            </div>
            <div className="hidden lg:flex items-center gap-2">
              <MapPin size={14} />
              <span>Triplicane High Road, Chennai</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
